import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Topbar from "../components/Topbar";
import PizzaSpinner from "../components/PizzaSpinner";
import ExpenseCategoryBadge from "../components/ExpenseCategoryBadge";
import { ArrowLeft, Save, Calendar, Building2, DollarSign, FileText, Hash, AlertCircle, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function ExpenseNew() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState(null);
  const [branches, setBranches] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    category: "",
    subcategory: "",
    vendor_name: "",
    vendor_rfc: "",
    vendor_bank_name: "",
    vendor_bank_account: "",
    description: "",
    subtotal: "",
    tax: "",
    total: "",
    billing_period_from: "",
    billing_period_to: "",
    reference_number: "",
    due_date: "",
    branch_id: user?.role === "branch_manager" ? user.branch_id : "",
    notes: "",
  });

  useEffect(() => {
    api.get("/expense-categories").then((r) => setCategories(r.data)).catch(() => setCategories([]));
    if (user?.role === "corporate_admin") {
      api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
      api.get("/recurring-expense-templates").then((r) => setTemplates(r.data.filter((tt) => tt.active))).catch(() => {});
    }
  }, [user]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    const s = parseFloat(form.subtotal) || 0;
    const tx = parseFloat(form.tax) || 0;
    if (s || tx) update("total", (s + tx).toFixed(2));
  }, [form.subtotal, form.tax]);

  const fillFromTemplate = (tplId) => {
    const tpl = templates.find((x) => x.id === tplId);
    if (!tpl) return;
    const locale = i18n.language?.startsWith("en") ? "en-US" : "es-MX";
    setForm((p) => ({
      ...p,
      category: tpl.category,
      subcategory: tpl.subcategory || "",
      vendor_name: tpl.vendor_name,
      vendor_rfc: tpl.vendor_rfc || "",
      vendor_bank_name: tpl.vendor_bank_name || "",
      vendor_bank_account: tpl.vendor_bank_account || "",
      description: `${tpl.name} — ${new Date().toLocaleString(locale, { month: "long", year: "numeric" })}`,
      subtotal: String(tpl.default_subtotal),
      tax: String(tpl.default_tax),
      total: String(tpl.default_total),
      branch_id: tpl.branch_id || "",
    }));
    toast.info(t("expenses.templateLoaded", { name: tpl.name }));
  };

  const submit = async () => {
    if (!form.category) return toast.error(t("expenses.selectCategoryError"));
    if (!form.vendor_name.trim()) return toast.error(t("expenses.providerRequiredError"));
    if (!form.description.trim()) return toast.error(t("expenses.descRequiredError"));
    if (!form.due_date) return toast.error(t("expenses.dueRequiredError"));
    if (parseFloat(form.total || 0) <= 0) return toast.error(t("expenses.totalGtZero"));
    setBusy(true);
    try {
      const payload = {
        category: form.category,
        subcategory: form.subcategory || null,
        vendor_name: form.vendor_name.trim(),
        vendor_rfc: form.vendor_rfc.trim() || null,
        vendor_bank_name: form.vendor_bank_name || null,
        vendor_bank_account: form.vendor_bank_account || null,
        description: form.description.trim(),
        subtotal: parseFloat(form.subtotal || 0),
        tax: parseFloat(form.tax || 0),
        total: parseFloat(form.total || 0),
        billing_period_from: form.billing_period_from || null,
        billing_period_to: form.billing_period_to || null,
        reference_number: form.reference_number.trim() || null,
        due_date: new Date(form.due_date).toISOString(),
        branch_id: form.branch_id || null,
        notes: form.notes.trim() || null,
      };
      const { data } = await api.post("/operating-expenses", payload);
      toast.success(t("expenses.savedToast", { folio: data.folio }));
      navigate(`/gastos/${data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("expenses.savingError"));
    } finally {
      setBusy(false);
    }
  };

  if (!categories) return <><Topbar title={t("expenses.newTitle")} /><PizzaSpinner /></>;

  const selectedCat = categories.find((c) => c.id === form.category);
  const subs = selectedCat?.subcategories || [];

  return (
    <>
      <Topbar title={t("expenses.newTitle")} subtitle={t("expenses.newSubtitle")} />
      <div className="p-6 max-w-4xl mx-auto space-y-5">
        <button data-testid="exp-back-btn" onClick={() => navigate("/gastos")}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#1A1A1A]">
          <ArrowLeft className="w-4 h-4" /> {t("expenses.backToList")}
        </button>

        {templates.length > 0 && (
          <div className="bg-[#FFFBE6] border border-[#FFDF00] rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <RotateCw className="w-4 h-4 text-[#1A1A1A]" />
              <span className="text-sm font-bold text-[#1A1A1A]">{t("expenses.isRecurringQ")}</span>
            </div>
            <p className="text-xs text-slate-700 mb-3">{t("expenses.templateHelp")}</p>
            <div className="flex flex-wrap gap-2">
              {templates.map((tpl) => (
                <button key={tpl.id} data-testid={`exp-tpl-btn-${tpl.id}`}
                  onClick={() => fillFromTemplate(tpl.id)}
                  className="px-3 py-1.5 bg-white border border-[#FFDF00] rounded-lg text-xs font-semibold hover:bg-[#FFF6BF]">
                  {tpl.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">{t("expenses.categoryRequired")}</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {categories.map((c) => (
                <button key={c.id} type="button" data-testid={`exp-cat-btn-${c.id}`}
                  onClick={() => { update("category", c.id); update("subcategory", ""); }}
                  className={`p-3 rounded-xl border text-left transition ${
                    form.category === c.id
                      ? "border-[#1A1A1A] bg-[#FFFBE6] ring-2 ring-[#FFDF00]"
                      : "border-slate-200 hover:border-slate-300"
                  }`}>
                  <div className="text-sm font-bold text-[#1A1A1A]">{t(`expense.categories.${c.id}`, { defaultValue: c.label })}</div>
                  {c.subcategories?.length > 0 && (
                    <div className="text-[10px] text-slate-500 mt-0.5">{t("expenses.subcategoriesCount", { count: c.subcategories.length })}</div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {subs.length > 0 && (
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">{t("expenses.subcatLabel")}</label>
              <div className="flex flex-wrap gap-2">
                {subs.map((s) => (
                  <button key={s.id} type="button" data-testid={`exp-sub-btn-${s.id}`}
                    onClick={() => update("subcategory", s.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${
                      form.subcategory === s.id
                        ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                        : "bg-white text-slate-700 border-slate-200 hover:border-slate-400"
                    }`}>
                    {t(`expense.subcategories.${s.id}`, { defaultValue: s.label })}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.providerRequired")}</label>
              <input data-testid="exp-vendor-input" value={form.vendor_name} onChange={(e) => update("vendor_name", e.target.value)}
                placeholder={t("expenses.providerPlaceholder")} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.rfcProvider")}</label>
              <input data-testid="exp-rfc-input" value={form.vendor_rfc}
                onChange={(e) => update("vendor_rfc", e.target.value.toUpperCase())}
                placeholder={t("common.optional")} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.descriptionRequired")}</label>
            <input data-testid="exp-desc-input" value={form.description} onChange={(e) => update("description", e.target.value)}
              placeholder={t("expenses.descriptionPh")} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>

          {user?.role === "corporate_admin" && (
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block flex items-center gap-1">
                <Building2 className="w-3 h-3" /> {t("expenses.appliesTo")}
              </label>
              <select data-testid="exp-branch-select" value={form.branch_id} onChange={(e) => update("branch_id", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                <option value="">{t("expenses.appliesToCorp")}</option>
                {branches.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block flex items-center gap-1">
                <DollarSign className="w-3 h-3" /> {t("expenses.subtotal")}
              </label>
              <input data-testid="exp-subtotal-input" type="number" min="0" step="0.01" value={form.subtotal}
                onChange={(e) => update("subtotal", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono text-right" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.tax")}</label>
              <input data-testid="exp-tax-input" type="number" min="0" step="0.01" value={form.tax}
                onChange={(e) => update("tax", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono text-right" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.totalRequired")}</label>
              <input data-testid="exp-total-input" type="number" min="0" step="0.01" value={form.total}
                onChange={(e) => update("total", e.target.value)}
                className="w-full px-3 py-2 border-2 border-[#FFDF00] rounded-lg text-sm font-mono text-right font-bold bg-[#FFFBE6]" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {t("expenses.dueRequired")}
              </label>
              <input data-testid="exp-due-input" type="date" value={form.due_date} onChange={(e) => update("due_date", e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.periodLabel")}</label>
              <div className="flex gap-1">
                <input data-testid="exp-period-from-input" type="date" value={form.billing_period_from}
                  onChange={(e) => update("billing_period_from", e.target.value)}
                  className="w-1/2 px-2 py-2 border border-slate-200 rounded-lg text-xs" />
                <input data-testid="exp-period-to-input" type="date" value={form.billing_period_to}
                  onChange={(e) => update("billing_period_to", e.target.value)}
                  className="w-1/2 px-2 py-2 border border-slate-200 rounded-lg text-xs" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block flex items-center gap-1">
                <Hash className="w-3 h-3" /> {t("expenses.providerFolio")}
              </label>
              <input data-testid="exp-ref-input" value={form.reference_number} onChange={(e) => update("reference_number", e.target.value)}
                placeholder={t("expenses.providerFolioPh")}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.bankForPay")}</label>
              <input data-testid="exp-bank-name-input" value={form.vendor_bank_name} onChange={(e) => update("vendor_bank_name", e.target.value)}
                placeholder={t("expenses.bankPh")} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">{t("expenses.clabe")}</label>
              <input data-testid="exp-bank-account-input" value={form.vendor_bank_account} onChange={(e) => update("vendor_bank_account", e.target.value)}
                placeholder={t("common.optional")} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" />
            </div>
          </div>

          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block flex items-center gap-1">
              <FileText className="w-3 h-3" /> {t("common.notes")}
            </label>
            <textarea data-testid="exp-notes-input" value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2}
              placeholder={t("expenses.additionalNotes")}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          </div>

          {form.category && (
            <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-3">
              <ExpenseCategoryBadge category={form.category} subcategory={form.subcategory} size="md" />
              <div className="text-[11px] text-slate-500">
                <AlertCircle className="inline w-3 h-3 mr-0.5" />
                {user?.role === "branch_manager"
                  ? t("expenses.branchHint")
                  : (form.branch_id ? t("expenses.corpHintBranch") : t("expenses.corpHintNoBranch"))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3">
          <button onClick={() => navigate("/gastos")}
            className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">
            {t("common.cancel")}
          </button>
          <button data-testid="exp-submit-btn" onClick={submit} disabled={busy}
            className="btn-primary-chizy px-5 py-2.5 rounded-xl flex items-center gap-2 disabled:opacity-50">
            <Save className="w-4 h-4" />
            {busy ? t("recurring.modal.saving") : t("expenses.saveExpense")}
          </button>
        </div>
      </div>
    </>
  );
}
